import {NavLink, useParams} from "react-router-dom";
import {useEffect} from "react";
import styled from "styled-components";
import {useCollection} from "../../cms/factory";
import ContentLayout from "../ContentLayout";
import {AsyncReveal} from "../AsyncReveal";
import {SText14W400, SText40W700, Text18} from "../Typography";
import {Button} from "../Button";
import DateChip from "../DateChip";
import {DocumentRenderer} from "@keystone-6/document-renderer";
import {YoutubeBlock} from "../YoutubeBlock";
import {ImageBlock} from "../ImageBlock";

interface NewsData {
    id: string;
    title: string;
    slug?: string;
    publishedAt?: string | null;
    mainImage?: { url?: string | null } | null;
    mainImageCaption?: string | null;
    fullText: {
        document: any;
    };
}

interface NewsNavItem {
    id: string;
    title: string;
    slug?: string;
    publishedAt?: string | null;
}

const SELECTION = `
    id
    title
    slug
    publishedAt
    mainImage { url }
    mainImageCaption
    fullText {
        document(hydrateRelationships: true)
    }
`;

const NAV_SELECTION = `
    id
    title
    slug
    publishedAt
`;

const NAV_ARGS = `
    where: {
        status: {
            equals: published
        }
    },
    orderBy: {
        publishedAt: desc
    }
`;

const PUBLISHED_STATUS = `status: { equals: published }`;

const NewsArticle = () => {
    const {slug} = useParams<{ slug: string }>();
    const safeSlug = slug?.replace(/"/g, '\\"') || "";
    const args = slug
        ? `where: { slug: { equals: "${safeSlug}" }, ${PUBLISHED_STATUS} }`
        : `where: { ${PUBLISHED_STATUS} }`;

    const {data, loading, error} = useCollection<NewsData>(
        "newsItem",
        SELECTION,
        args
    );

    const {data: navList, loading: navLoading, error: navError} = useCollection<NewsNavItem>(
        "newsItem",
        NAV_SELECTION,
        NAV_ARGS
    );

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [slug]);

    const article = data?.[0];
    const list = navList ?? [];
    const currentIndex = slug ? list.findIndex(item => item.slug === slug) : -1;
    const previousArticle = currentIndex > 0 ? list[currentIndex - 1] : null;
    const nextArticle = currentIndex >= 0 ? list[currentIndex + 1] : null;
    const showNav = article && slug && !navLoading && !navError;

    return (
        <ContentLayout backTo="/news" loading={loading}>
            <AsyncReveal loading={loading} error={error} loaderDelay={0.5}>
                <Wrapper>
                    {!article && !loading && (
                        <EmptyState>{!slug ? 'Missing article slug.' : 'News article not found.'}</EmptyState>
                    )}
                    {article && <>
                        {article.publishedAt && <DateChip ISO={article.publishedAt}/>}
                            <SText40W700 as="h1">{article.title}</SText40W700>

                            <Body>
                                {article.mainImage?.url && (
                                    <Figure>
                                        <Img src={article.mainImage.url} alt={article.title}/>
                                        <ImgCaption>{article.mainImageCaption}</ImgCaption>
                                    </Figure>
                                )}
                                {article.fullText?.document && (
                                    <DocumentRenderer
                                        document={article.fullText.document}
                                    componentBlocks={{
                                        image: ImageBlock,
                                        youtube: YoutubeBlock,
                                    }}
                                    />
                                )}
                            </Body>
                    </>
                    }
                    {showNav && (previousArticle || nextArticle) && (
                        <Nav aria-label="Section navigation">
                            {previousArticle?.slug && (
                                <PreviousButton to={`/news/${previousArticle.slug}`} $variant="white">Previous
                                    news</PreviousButton>
                            )}
                            {nextArticle?.slug && (
                                <NextButton to={`/news/${nextArticle.slug}`}>Next news</NextButton>
                            )}
                        </Nav>
                    )}
                </Wrapper>
            </AsyncReveal>
        </ContentLayout>
    );
}

export default NewsArticle;

const Wrapper = styled.article`
    display: flex;
    flex-direction: column;
    gap: 24px;
`

const EmptyState = styled(Text18)`
    text-align: center;
`;

const Nav = styled.nav`
    display: flex;
    justify-content: space-between;
    gap: 24px;

    @media (max-width: 480px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const Body = styled(Text18).attrs({as: 'div'})`
    white-space: pre-line;
    letter-spacing: -0.3px;

    @media (max-width: 768px) {
        font-size: 16px;
        line-height: 20px;
    }

    h2, h3 {
        margin-bottom: 8px;
        font-weight: 700;
    }

    h2 {
        font-size: 24px;
        line-height: 28px;

        @media (max-width: 768px) {
            font-size: 20px;
            line-height: 24px;
        }
    }

    p {
        margin-bottom: 24px;

        &:last-child {
            margin-bottom: 0;
        }
    }

    a {
        color: #16a34a;
    }
`

const Figure = styled.figure`
    display: flex;
    flex-direction: column;
    gap: 12px;
    float: right;
    width: 592px;
    margin-left: 48px;
    margin-bottom: 24px;
    
    @media (max-width: 950px) {
        width: 100%;
        margin-left: 0;
    }
`;

const Img = styled.img`
    width: 100%;
    object-fit: contain;
    border-radius: 16px;
`

const ImgCaption = styled(SText14W400).attrs({as: 'figcaption'})`
    display: block;
    text-align: left;
    width: 100%;
    line-height: 16px;
`

const NavButton = styled(Button).attrs({as: NavLink})`
    padding: 16px 32px;
    font-size: 18px;
    line-height: 24px;
    font-weight: 500;

    @media (max-width: 768px) {
        font-size: 16px;
        line-height: 20px;
    }

    @media (max-width: 480px) {
        width: 100%;
    }
`;

const PreviousButton = styled(NavButton)`
    background-color: #F6F6F6;
    margin-right: auto;
    color: #242624;
`

const NextButton = styled(NavButton)`
    margin-left: auto;
`
